import React from 'react';

import { ThumbnailProps } from '@lumapps/lumx/react';
import castArray from 'lodash/castArray';

import { OneOrMany } from '@lumapps/utils/types/OneOrMany';

import { ImageLightboxContext } from '.';
import { actions } from './slice';

/**
 * Function to call to open the image callback
 */
export type OpenLightboxCallback = (parentElement: HTMLElement, activeIndex?: number) => void;

/**
 * Get a callback to open images in the parent ImageLightboxContext
 */
export const useRegisterLightBoxImage = (images: OneOrMany<ThumbnailProps>): undefined | OpenLightboxCallback => {
    const dispatchAction = React.useContext(ImageLightboxContext);
    // List of local images
    const localImages = React.useMemo(() => castArray(images), [images]);

    // Register the local images to the global slideshow in the parent context
    React.useEffect(() => {
        if (!dispatchAction) {
            return undefined;
        }

        // Register on mount
        dispatchAction(actions.register(localImages));
        return () => {
            // Un-register on unmount
            dispatchAction(actions.unregister(localImages));
        };
    }, [dispatchAction, localImages]);

    // Open lightbox callback
    return React.useMemo(() => {
        if (!dispatchAction) {
            return undefined;
        }
        return (parentElement, localeActiveIndex = 0) => {
            if (!dispatchAction || !localImages.length) {
                return;
            }
            const activeImage = localImages[localeActiveIndex];
            dispatchAction(actions.open({ activeImage, parentElement }));
        };
    }, [dispatchAction, localImages]);
};
